import React, { useEffect, useMemo } from "react"
import { format, add, differenceInMinutes } from "date-fns"
import moment from "moment"

type TimeOptionsProps = {
  availabilityStart: Date
  availabilityEnd: Date
  duration: number
  startTime: Date | null
  setStartTime: (date: Date) => void
  dayColor: string
  prepopulatedStartTime?: Date | null
  bookingWindow: number
}

const TimeOptions: React.FC<TimeOptionsProps> = ({
  availabilityStart,
  availabilityEnd,
  duration,
  setStartTime,
  startTime,
  dayColor,
  prepopulatedStartTime,
  bookingWindow,
}) => {
  const MEETING_START_TIME_INTERVAL = 15

  useEffect(() => {
    if (prepopulatedStartTime) {
      setStartTime(new Date(prepopulatedStartTime))
    }
  }, [prepopulatedStartTime, setStartTime])

  const bookingThreshold = useMemo(() => {
    const threshold = moment().add(bookingWindow, "hours").toDate()
    return threshold
  }, [bookingWindow])

  const generateStartTimes = (
    start: Date,
    end: Date,
    duration: number
  ): Array<Date> => {
    const options: Date[] = []

    let adjustedStart = start
    const quarterMinutes = [0, 15, 30, 45]
    const startMinutes = moment(start).minutes()
    if (!quarterMinutes.includes(startMinutes)) {
      const minutesToAdd = 15 - (startMinutes % 15)
      adjustedStart = moment(start).add(minutesToAdd, "minutes").toDate()
    }

    const numIntervals =
      differenceInMinutes(end, adjustedStart) / MEETING_START_TIME_INTERVAL

    for (let i = 0; i < numIntervals; i++) {
      const slot = add(adjustedStart, {
        minutes: MEETING_START_TIME_INTERVAL * i,
      })

      if (add(slot, { minutes: duration }) > end) break

      options.push(slot)
    }

    return options
  }

  const startTimes = useMemo(() => {
    return generateStartTimes(availabilityStart, availabilityEnd, duration)
  }, [availabilityStart, availabilityEnd, duration])

  return (
    <>
      {startTimes.map((t, idx) => {
        const startLabel = format(t, "h:mm aa")
        const endLabel = format(add(t, { minutes: duration }), "h:mm aa")
        const effectiveStartTime = startTime ?? prepopulatedStartTime
        const isSelected = effectiveStartTime
          ? moment(effectiveStartTime).isSame(moment(t))
          : false

        const isUnavailable = t < bookingThreshold

        return (
          <button
            key={idx}
            onClick={() => {
              if (!isUnavailable) setStartTime(t)
            }}
            disabled={isUnavailable}
            className={`flex items-center justify-center p-[12px] rounded-[10px] text-base font-bold
              ${isSelected ? "text-white" : "bg-hoverDark"}
              ${
                isUnavailable
                  ? "opacity-50 cursor-not-allowed bg-gray-300"
                  : "cursor-pointer"
              }
            `}
            style={{
              backgroundColor: isSelected ? dayColor : "",
            }}
            aria-label={`${startLabel} to ${endLabel}`}
          >
            {isUnavailable ? "Unavailable" : `${startLabel} - ${endLabel}`}
          </button>
        )
      })}
    </>
  )
}

export default TimeOptions
